/* eslint-disable react-hooks/exhaustive-deps */

import { FC, useState, useEffect, MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import triggerTracking from '@blueprint/tracking';
import { Text } from '@blueprint/ui';

import { Envelope } from '@zoopla/assets';
import {
    Content,
    StyledButtonLink,
    StyledHeading,
    StyledIcon,
    StyledImage
} from './AlertCtaModal.styles';
import { selectPageNumber } from '@zoopla/redux-reducer-search-results';
import { alertLinkClickHandler } from '@zoopla/listing-search/nav-with-auth';
import { Links } from '@zoopla/listing-search-context';
import { useIsLoggedIn } from '@zoopla/redux-reducer-user';
import { StyledModal } from '@zoopla/listing-search/alert-panel';

const triggerAlertPopupTracking = (eventLabel: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'Alert pop up',
        eventAction: 'clickthrough',
        eventLabel
    });
};

type AlertCtaModalProps = {
    links: Links;
};

export const AlertCtaModal: FC<React.PropsWithChildren<AlertCtaModalProps>> = ({
    links
}) => {
    const pageNumber = useSelector(selectPageNumber);
    const isLoggedIn = useIsLoggedIn();

    const [isModalShowing, setIsModalShowing] = useState(false);
    const [willShowModal, setWillShowModal] = useState(false);
    const router = useRouter();

    useEffect(() => {
        const showModal =
            pageNumber >= 2 &&
            isLoggedIn === false &&
            typeof window !== 'undefined' &&
            !sessionStorage.getItem('alertsPopupClose');
        setWillShowModal(showModal);
    }, [
        pageNumber,
        isLoggedIn,
        typeof window !== 'undefined' &&
            sessionStorage.getItem('alertsPopupClose')
    ]);

    useEffect(() => {
        setIsModalShowing(sessionStorage.getItem('alertsPopupClose') === null);
    }, []);

    useEffect(() => {
        if (willShowModal) {
            triggerAlertPopupTracking('Alert pop up open');
        }
    }, [willShowModal]);

    if (!willShowModal || !isModalShowing) {
        return null;
    }

    const closeModal = () => {
        setIsModalShowing(false);
        sessionStorage.setItem('alertsPopupClose', '1');
    };

    const onButtonClick = (evt: MouseEvent<HTMLAnchorElement>) => {
        evt.preventDefault();

        triggerAlertPopupTracking('Alert pop up click');

        sessionStorage.setItem('alertsPopupClose', '1');
        alertLinkClickHandler({
            evt,
            router,
            requiresAuth: true,
            isLoggedIn
        });
    };

    return (
        <StyledModal isShowing={isModalShowing} close={closeModal}>
            <Content>
                <StyledImage src={Envelope} alt="Envelope image" />
                <StyledHeading size="5">Sign up for email alerts</StyledHeading>
                <Text size="secondary">
                    Be the first to know about new properties matching your
                    search criteria
                </Text>
                <StyledButtonLink
                    onClick={onButtonClick}
                    data-url={links?.createAlert || ''}
                >
                    Create alert
                </StyledButtonLink>
                <Text size="small">
                    <StyledIcon iconId="padlock" />
                    We will not share your details with third parties
                </Text>
            </Content>
        </StyledModal>
    );
};
